import { create } from '@signals/app-shell/shims/zustand';
import { format, parseISO } from 'date-fns';
import * as itineraryApi from '../services/itineraryApi';

interface ItinerarySubmissionFormState {
  savedSubmissionId: number | null;
  studentNumber: string;
  routeRunDateTime: string;
  gender: string;
  height: string;
  weight: string;
  hairTypeColor: string;
  facialHairTypeColor: string;
  vehMake: string;
  vehModel: string;
  vehColor: string;
  vehPlate: string;
  routeEntries: itineraryApi.RouteDataEntry[];
  notes: string;
  uploadedFiles: File[];
  currentEntryIndex: number;
  setField: <K extends keyof ItinerarySubmissionFormValues>(field: K, value: ItinerarySubmissionFormValues[K]) => void;
  setRouteEntries: (entries: itineraryApi.RouteDataEntry[]) => void;
  setUploadedFiles: (files: File[] | ((files: File[]) => File[])) => void;
  setCurrentEntryIndex: (index: number) => void;
  loadSubmission: (submission: itineraryApi.ItinerarySubmission) => void;
  resetForm: () => void;
}

type ItinerarySubmissionFormValues = Pick<
  ItinerarySubmissionFormState,
  | 'studentNumber'
  | 'routeRunDateTime'
  | 'gender'
  | 'height'
  | 'weight'
  | 'hairTypeColor'
  | 'facialHairTypeColor'
  | 'vehMake'
  | 'vehModel'
  | 'vehColor'
  | 'vehPlate'
  | 'notes'
  | 'savedSubmissionId'
>;

const formatForDateTimeInput = (dateTime?: string) => {
  if (!dateTime) return format(new Date(), "yyyy-MM-dd'T'HH:mm");
  try {
    return format(parseISO(dateTime), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return dateTime.slice(0, 16);
  }
};

const initialFormState = () => ({
  savedSubmissionId: null,
  studentNumber: '',
  routeRunDateTime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
  gender: '',
  height: '',
  weight: '',
  hairTypeColor: '',
  facialHairTypeColor: '',
  vehMake: '',
  vehModel: '',
  vehColor: '',
  vehPlate: '',
  routeEntries: [],
  notes: '',
  uploadedFiles: [],
  currentEntryIndex: 0,
});

export const useItinerarySubmissionStore = create<ItinerarySubmissionFormState>((set) => ({
  ...initialFormState(),
  setField: (field, value) => set({ [field]: value } as Pick<ItinerarySubmissionFormState, typeof field>),
  setRouteEntries: (routeEntries) => set((state) => ({
    routeEntries,
    currentEntryIndex: Math.min(state.currentEntryIndex, Math.max(0, routeEntries.length - 1)),
  })),
  setUploadedFiles: (files) => set((state) => ({
    uploadedFiles: typeof files === 'function' ? files(state.uploadedFiles) : files,
  })),
  setCurrentEntryIndex: (currentEntryIndex) => set({ currentEntryIndex }),
  loadSubmission: (submission) => set({
    savedSubmissionId: submission.id || null,
    studentNumber: submission.student_number || '',
    routeRunDateTime: formatForDateTimeInput(submission.route_run_datetime),
    gender: submission.gender || '',
    height: submission.height || '',
    weight: submission.weight || '',
    hairTypeColor: submission.hair_typecolor || '',
    facialHairTypeColor: submission.facial_hair_typecolor || '',
    vehMake: submission.veh_make || '',
    vehModel: submission.veh_model || '',
    vehColor: submission.veh_color || '',
    vehPlate: submission.veh_plate || '',
    routeEntries: (submission.route_entries || []).map((entry, index) => ({
      ...entry,
      entry_order: index,
      arrive_datetime: formatForDateTimeInput(entry.arrive_datetime),
      depart_datetime: formatForDateTimeInput(entry.depart_datetime),
    })),
    notes: submission.notes || '',
    uploadedFiles: [],
    currentEntryIndex: 0,
  }),
  resetForm: () => set(initialFormState()),
}));
